
import type { PaymentPoliciesStore } from './interfaces'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { PolicyEdit, TutorPaymentPolicy } from '@entities/tutor'

import { TutorCategoryType } from '@entities/tutor'
import { isFullArray } from '@shared/lib/collections'

const getOptions = (state: PaymentPoliciesStore): AppSelectOption[] => state.paginatedData?.items || []
const getMeta = (state: PaymentPoliciesStore): Meta | null => state.paginatedData?.meta || null

const getSelectedDemoEla = (state: PaymentPoliciesStore): PaymentPoliciesStore['selectedDemoEla'] => state.selectedDemoEla
const getSelectedDemoMath = (state: PaymentPoliciesStore): PaymentPoliciesStore['selectedDemoMath'] => state.selectedDemoMath
const getSelectedPaidMath = (state: PaymentPoliciesStore): PaymentPoliciesStore['selectedPaidMath'] => state.selectedPaidMath
const getSelectedPaidEla = (state: PaymentPoliciesStore): PaymentPoliciesStore['selectedPaidEla'] => state.selectedPaidEla

const computeEdits = (state: PaymentPoliciesStore): PolicyEdit[] => {
  const savedByCategory = new Map<TutorCategoryType, number>(
    state.tutorPaymentPoliciesData?.payment_policies.map(p => [p.category, p.id]),
  )

  const selection: [TutorCategoryType, number | null][] = [
    [TutorCategoryType.DemoMath, getSelectedDemoMath(state) as number | null],
    [TutorCategoryType.DemoEla, getSelectedDemoEla(state) as number | null],
    [TutorCategoryType.PaidMath, getSelectedPaidMath(state) as number | null],
    [TutorCategoryType.PaidEla, getSelectedPaidEla(state) as number | null],
  ]

  const edits: PolicyEdit[] = []
  for (const [category, value] of selection) {
    const saved = savedByCategory.get(category) ?? null
    if (saved === value) continue
    edits.push({
      id: value,
      category,
      name: state.selectedNames[category] ?? '',
    })
  }
  return edits
}

export const getters = {
  getOptions: (state: PaymentPoliciesStore): AppSelectOption[] => getOptions(state),
  isOptions: (state: PaymentPoliciesStore): boolean => isFullArray(getOptions(state)),
  getMeta: (state: PaymentPoliciesStore): Meta | null => getMeta(state),
  getSelectedDemoEla: (state: PaymentPoliciesStore): PaymentPoliciesStore['selectedDemoEla'] => getSelectedDemoEla(state),
  getSelectedDemoMath: (state: PaymentPoliciesStore): PaymentPoliciesStore['selectedDemoMath'] => getSelectedDemoMath(state),
  getSelectedPaidMath: (state: PaymentPoliciesStore): PaymentPoliciesStore['selectedPaidMath'] => getSelectedPaidMath(state),
  getSelectedPaidEla: (state: PaymentPoliciesStore): PaymentPoliciesStore['selectedPaidEla'] => getSelectedPaidEla(state),
  isSelectedDemoEla: (state: PaymentPoliciesStore): boolean => !!getSelectedDemoEla(state),
  isSelectedDemoMath: (state: PaymentPoliciesStore): boolean => !!getSelectedDemoMath(state),
  isSelectedPaidMath: (state: PaymentPoliciesStore): boolean => !!getSelectedPaidMath(state),
  isSelectedPaidEla: (state: PaymentPoliciesStore): boolean => !!getSelectedPaidEla(state),
  getTutorPolicies: (state: PaymentPoliciesStore): TutorPaymentPolicy[] => state.tutorPaymentPoliciesData?.payment_policies || [],
  getTutorId: (state: PaymentPoliciesStore): number => state.tutorPaymentPoliciesData?.id || 0,
  isTutorPolicies: (state: PaymentPoliciesStore): boolean => isFullArray(state.tutorPaymentPoliciesData?.payment_policies),
  getEdits: (state: PaymentPoliciesStore): PolicyEdit[] => state.edits,
  getDraftEdits: (state: PaymentPoliciesStore): PolicyEdit[] => computeEdits(state),
  hasPolicyChanges: (state: PaymentPoliciesStore): boolean => computeEdits(state).length > 0,
  isLoading: (state: PaymentPoliciesStore): PaymentPoliciesStore['loading'] => state.loading,
}
